"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "./variants";
import { Spinner } from "./spinner";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  testId?: string;
  /**
   * variant="field" only: the trailing chevron that says the box holds a value picked
   * from a list or panel (BRE-8715). False for an entry point, a search or add box
   * whose picks land somewhere else.
   */
  chevron?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      testId = "button",
      chevron = true,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const content = isLoading ? <Spinner size="sm" tone="current" decorative /> : children;
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          "relative",
          className
        )}
        ref={ref}
        disabled={isLoading}
        aria-busy={isLoading || undefined}
        data-testid={testId}
        {...props}
      >
        {/* asChild's Slot takes exactly one child (not even a trailing `false`), so a slotted field draws its own. */}
        {variant === "field" && chevron && !asChild ? (
          <>
            {content}
            <ChevronDown className="opacity-50" aria-hidden />
          </>
        ) : (
          content
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button };
